import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';

function AdminPanel() {
  const token = localStorage.getItem('adminToken');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    location: '',
    year: '',
    category: 'installations',
    rating: '5',
    description: ''
  });
  const [image, setImage] = useState(null);
  const [message, setMessage] = useState('');

  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryImages, setGalleryImages] = useState({});
  const [galleryMessage, setGalleryMessage] = useState('');

  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    name: '',
    location: '',
    rating: '5',
    text: ''
  });
  const [reviewMessage, setReviewMessage] = useState('');

  useEffect(() => {
    fetch('/api/reviews')
      .then(res => res.json())
      .then(data => setReviews(data))
      .catch(err => console.error('Failed to load reviews', err));
  }, []);

  useEffect(() => {
    fetch('/api/gallery')
      .then(res => res.json())
      .then(data => setGalleryItems(data))
      .catch(err => console.error('Failed to load gallery', err));
  }, []);

  if (!token) {
    navigate('/login');
  }

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReviewFieldChange = (id, field, value) => {
    setReviews(reviews.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleNewReviewChange = e => {
    setNewReview({ ...newReview, [e.target.name]: e.target.value });
  };

  const addReview = e => {
    e.preventDefault();
    fetch('/api/reviews', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(newReview)
    })
      .then(res => res.json())
      .then(data => {
        if (data.review) {
          setReviews([...reviews, data.review]);
          setNewReview({ name: '', location: '', rating: '5', text: '' });
          setReviewMessage('Review added');
        }
      })
      .catch(() => setReviewMessage('Failed to add review'));
  };

  const saveReview = id => {
    const review = reviews.find(r => r.id === id);
    fetch(`/api/reviews/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(review)
    })
      .then(res => res.json())
      .then(data => {
        if (data.review) {
          setReviews(reviews.map(r => r.id === id ? data.review : r));
          setReviewMessage('Review updated');
        }
      })
      .catch(() => setReviewMessage('Failed to update review'));
  };

  const handleGalleryFieldChange = (id, field, value) => {
    setGalleryItems(galleryItems.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const handleGalleryImageChange = (id, file) => {
    setGalleryImages({ ...galleryImages, [id]: file });
  };

  const saveGalleryItem = id => {
    const item = galleryItems.find(i => i.id === id);
    const data = new FormData();
    data.append('title', item.title);
    data.append('location', item.location);
    data.append('year', item.year);
    data.append('category', item.category);
    data.append('rating', item.rating);
    data.append('description', item.description);
    if (galleryImages[id]) {
      data.append('image', galleryImages[id]);
    }

    fetch(`/api/gallery/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(data => {
        if (data.item) {
          setGalleryItems(galleryItems.map(i => i.id === id ? data.item : i));
          setGalleryImages(prev => ({ ...prev, [id]: undefined }));
          setGalleryMessage('Gallery item updated');
        }
      })
      .catch(() => setGalleryMessage('Failed to update gallery item'));
  };

  const deleteGalleryItem = id => {
    fetch(`/api/gallery/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(() => {
        setGalleryItems(galleryItems.filter(i => i.id !== id));
        setGalleryMessage('Gallery item deleted');
      })
      .catch(() => setGalleryMessage('Failed to delete gallery item'));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!image) return;

    const data = new FormData();
    data.append('image', image);
    Object.keys(formData).forEach(key => data.append(key, formData[key]));

    fetch('/api/gallery/upload', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(() => setMessage('Upload successful'))
      .catch(() => setMessage('Upload failed'));
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <h2>Admin Panel - Upload Image</h2>
        <form className="admin-form" onSubmit={handleSubmit}>
          <label>Image
            <input type="file" accept="image/*" onChange={e => setImage(e.target.files[0])} required />
          </label>
          <label>Title
            <input name="title" value={formData.title} onChange={handleChange} required />
          </label>
          <label>Location
            <input name="location" value={formData.location} onChange={handleChange} />
          </label>
          <label>Year
            <input name="year" value={formData.year} onChange={handleChange} />
          </label>
          <label>Category
            <select name="category" value={formData.category} onChange={handleChange}>
              <option value="installations">Installations</option>
              <option value="repairs">Repairs</option>
              <option value="traditional">Traditional</option>
            </select>
          </label>
          <label>Rating
            <input name="rating" type="number" min="0" max="5" step="0.1" value={formData.rating} onChange={handleChange} />
          </label>
          <label>Description
            <textarea name="description" rows="3" value={formData.description} onChange={handleChange} />
          </label>
          <button type="submit">Upload</button>
          {message && <p className="admin-message">{message}</p>}
        </form>

        <h2 style={{ marginTop: '2rem' }}>Manage Gallery</h2>
        {galleryMessage && <p className="admin-message">{galleryMessage}</p>}
        <div className="admin-gallery-list">
          {galleryItems.map(item => (
            <div key={item.id} className="admin-gallery-item">
              <img src={item.image} alt={item.title} />
              <input value={item.title} onChange={e => handleGalleryFieldChange(item.id, 'title', e.target.value)} />
              <input value={item.location} onChange={e => handleGalleryFieldChange(item.id, 'location', e.target.value)} />
              <input value={item.year} onChange={e => handleGalleryFieldChange(item.id, 'year', e.target.value)} />
              <select value={item.category} onChange={e => handleGalleryFieldChange(item.id, 'category', e.target.value)}>
                <option value="installations">Installations</option>
                <option value="repairs">Repairs</option>
                <option value="traditional">Traditional</option>
              </select>
              <input type="number" min="0" max="5" step="0.1" value={item.rating} onChange={e => handleGalleryFieldChange(item.id, 'rating', e.target.value)} />
              <textarea rows="3" value={item.description} onChange={e => handleGalleryFieldChange(item.id, 'description', e.target.value)} />
              <input type="file" accept="image/*" onChange={e => handleGalleryImageChange(item.id, e.target.files[0])} />
              <div className="action-buttons">
                <button type="button" onClick={() => saveGalleryItem(item.id)}>Save</button>
                <button type="button" onClick={() => deleteGalleryItem(item.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>

        <h2 style={{ marginTop: '2rem' }}>Manage Reviews</h2>
        <form className="admin-form" onSubmit={addReview}>
          <label>Name
            <input name="name" value={newReview.name} onChange={handleNewReviewChange} required />
          </label>
          <label>Location
            <input name="location" value={newReview.location} onChange={handleNewReviewChange} />
          </label>
          <label>Rating
            <input name="rating" type="number" min="0" max="5" step="0.1" value={newReview.rating} onChange={handleNewReviewChange} />
          </label>
          <label>Text
            <textarea name="text" value={newReview.text} onChange={handleNewReviewChange} required />
          </label>
          <button type="submit">Add Review</button>
        </form>
        {reviewMessage && <p className="admin-message">{reviewMessage}</p>}

        <div className="review-list">
          {reviews.map(r => (
            <div key={r.id} className="review-item">
              <input value={r.name} onChange={e => handleReviewFieldChange(r.id, 'name', e.target.value)} />
              <input value={r.location} onChange={e => handleReviewFieldChange(r.id, 'location', e.target.value)} />
              <input type="number" min="0" max="5" step="0.1" value={r.rating} onChange={e => handleReviewFieldChange(r.id, 'rating', e.target.value)} />
              <textarea value={r.text} onChange={e => handleReviewFieldChange(r.id, 'text', e.target.value)} />
              <button type="button" onClick={() => saveReview(r.id)}>Save</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
